import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import "./ec_v2.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const api = (path, opts = {}) =>
  fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  }).then(async (r) => {
    if (!r.ok) throw new Error(`API error ${r.status}`);
    return r.json();
  });

/* ───────── Stepper — all 6 steps, 1-5 done, 6 active ───────── */
const Stepper = () => {
  const steps = [
    { label: "Assessment", state: "done" },
    { label: "Scope", state: "done" },
    { label: "Scope of Services", state: "done" },
    { label: "Payment", state: "done" },
    { label: "Plan", state: "done" },
    { label: "Workspace", state: "active", num: 6 },
  ];
  return (
    <div className="ec-stepper" data-testid="tracker-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className="ec-step-item">
            <div className={`ec-step-circle ${s.state}`}>
              {s.state === "done" ? "✓" : s.num}
            </div>
            <div className={`ec-step-label ${s.state}`}>{s.label}</div>
          </div>
          {i < steps.length - 1 && (
            <div className={`ec-step-line ${steps[i].state === "done" ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ───────── Date helpers ───────── */
const fmtDate = (iso) => {
  if (!iso) return "TBD";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "TBD";
  }
};
const fmtMonthDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  return Math.max(0, Math.ceil((d - Date.now()) / 86400000));
};

/* ───────── KPI Strip ───────── */
const KpiCard = ({ label, icon, value, sub, bar, barColor }) => (
  <div
    className="ec-card"
    style={{ marginBottom: 0, padding: "16px 18px", position: "relative", overflow: "hidden" }}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "var(--ec-ink4)",
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {icon}
      {label}
    </div>
    <div
      style={{
        fontSize: 28,
        fontWeight: 700,
        color: barColor === "var(--ec-blue)" ? "var(--ec-blue)" : "var(--ec-ink)",
        lineHeight: 1,
        marginBottom: 4,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 11, color: "var(--ec-ink4)" }}>{sub}</div>
    {typeof bar === "number" && (
      <div
        style={{
          height: 3,
          background: "var(--ec-rule2)",
          borderRadius: 2,
          marginTop: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(100, Math.max(0, bar))}%`,
            background: barColor || "var(--ec-blue)",
            borderRadius: 2,
            transition: "width 0.8s ease",
          }}
        />
      </div>
    )}
  </div>
);

const KpiStrip = ({ workflows, milestones, checklist, progress, daysLeft }) => (
  <div
    className="ec-kpi-strip fade-up"
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 12,
      marginBottom: 22,
      animationDelay: "0.05s",
    }}
    data-testid="kpi-strip"
  >
    <KpiCard
      label="Workflows"
      icon={<span>☰</span>}
      value={workflows.total}
      sub="AI workflows in scope"
    />
    <KpiCard
      label="Milestones"
      icon={<span>◎</span>}
      value={
        <>
          {milestones.done}
          <span style={{ fontSize: 16, color: "var(--ec-ink4)", fontWeight: 500 }}>/{milestones.total}</span>
        </>
      }
      sub={`${milestones.done} complete · ${milestones.total - milestones.done} remaining`}
      bar={milestones.total ? (milestones.done / milestones.total) * 100 : 0}
    />
    <KpiCard
      label="Checklist Items"
      icon={<span>✓</span>}
      value={
        <>
          {checklist.done}
          <span style={{ fontSize: 16, color: "var(--ec-ink4)", fontWeight: 500 }}>/{checklist.total}</span>
        </>
      }
      sub={`${checklist.done} done · ${Math.max(0, checklist.total - checklist.done)} remaining`}
      bar={checklist.total ? (checklist.done / checklist.total) * 100 : 0}
    />
    <KpiCard
      label="Overall Progress"
      icon={<span>▲</span>}
      value={
        <>
          {progress}
          <span style={{ fontSize: 18 }}>%</span>
        </>
      }
      sub={daysLeft != null ? `On track · ${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining` : "On track"}
      bar={progress}
      barColor="var(--ec-blue)"
    />
    <style>{`
      @media (max-width: 1024px) {
        .ec-v2 .ec-kpi-strip { grid-template-columns: repeat(2, 1fr) !important; }
      }
      @media (max-width: 680px) {
        .ec-v2 .ec-kpi-strip { grid-template-columns: 1fr 1fr !important; }
      }
    `}</style>
  </div>
);

/* ───────── Gantt Timeline ───────── */
const PHASE_STATUS_CLASS = {
  complete: "ph-complete",
  active: "ph-active",
  in_progress: "ph-active",
  blocked: "ph-blocked",
  pending: "ph-pending",
  upcoming: "ph-pending",
};

const GanttCard = ({ phases, startDate, endDate }) => {
  const totalWeeks = 5; // W0..W4 per brief
  return (
    <div className="ec-card fade-up" style={{ animationDelay: "0.1s" }} data-testid="gantt-card">
      <div className="ec-card-header">
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ec-ink)", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="var(--ec-blue)" strokeWidth="1.5">
            <rect x="1" y="2" width="14" height="13" rx="2" />
            <path d="M1 6h14M5 1v2M11 1v2" />
          </svg>
          Project Timeline
        </div>
        <div style={{ fontSize: 12, color: "var(--ec-ink4)", fontWeight: 500 }}>
          {fmtMonthDay(startDate)} — {fmtMonthDay(endDate)}
        </div>
      </div>
      <div style={{ padding: "0 20px 16px" }}>
        {/* Column labels */}
        <div
          className="gantt-week-row"
          style={{
            display: "grid",
            gridTemplateColumns: "220px 1fr 1fr 1fr 1fr 1fr",
            paddingBottom: 8,
            paddingTop: 6,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--ec-ink4)" }}>Phase</div>
          {Array.from({ length: totalWeeks }).map((_, i) => {
            const wkDate = startDate
              ? new Date(new Date(startDate).getTime() + i * 7 * 86400000)
              : null;
            return (
              <div
                key={i}
                style={{ fontSize: 10, fontWeight: 600, color: "var(--ec-ink4)", textAlign: "center" }}
              >
                W{i}
                {wkDate && ` — ${wkDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
              </div>
            );
          })}
        </div>

        {phases.map((p, i) => {
          const statusClass = PHASE_STATUS_CLASS[p.status] || "ph-pending";
          const startWeek = Math.max(0, Math.min(totalWeeks - 1, p.start_week ?? i));
          const fillPct = Math.max(0, Math.min(100, p.progress ?? (p.status === "complete" ? 100 : 0)));
          const barLabel = p.status === "complete" ? "100%" : fillPct ? `${fillPct}%` : "";
          const icon = p.status === "complete" ? "✓" : p.status === "active" || p.status === "in_progress" ? "→" : "◌";
          return (
            <div
              key={p.id || i}
              className={`gantt-phase ${statusClass}`}
              style={{
                display: "grid",
                gridTemplateColumns: "220px 1fr 1fr 1fr 1fr 1fr",
                alignItems: "center",
                padding: "7px 0",
                borderBottom: i === phases.length - 1 ? "none" : "1px solid var(--ec-rule)",
              }}
              data-testid={`gantt-phase-${i}`}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--ec-ink2)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingRight: 12,
                }}
              >
                {icon} {p.name}
              </div>
              {Array.from({ length: totalWeeks }).map((_, w) => {
                if (w !== startWeek) return <div key={w} style={{ height: 22 }} />;
                // A-06 (May-2026): every phase bar uses the same Executive Clarity
                // blue palette so all 5 bars are visible. Status only changes the
                // FILL percentage, never the colour. Complete phases keep their
                // green accent for clarity.
                const isComplete = statusClass === "ph-complete";
                const isBlocked = statusClass === "ph-blocked";
                const outerBg = isComplete
                  ? "var(--ec-grn-l)"
                  : isBlocked
                    ? "var(--ec-red-l)"
                    : "var(--ec-blue-l)";
                const fillBg = isComplete
                  ? "var(--ec-green)"
                  : isBlocked
                    ? "var(--ec-red)"
                    : "var(--ec-blue)";
                return (
                  <div
                    key={w}
                    style={{
                      height: 22,
                      borderRadius: 5,
                      background: outerBg,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${fillPct}%`,
                        background: fillBg,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 5,
                        minWidth: fillPct > 0 ? 28 : 0,
                        transition: "width 0.8s ease",
                      }}
                    >
                      {barLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <style>{`
        @media (max-width: 768px) {
          .ec-v2 .gantt-week-row,
          .ec-v2 .gantt-phase { grid-template-columns: 140px 1fr 1fr 1fr 1fr 1fr !important; }
        }
        @media (max-width: 680px) {
          .ec-v2 .gantt-week-row,
          .ec-v2 .gantt-phase { grid-template-columns: 110px 1fr 1fr 1fr 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
};

/* ───────── Task Cards ───────── */
const TAG_STYLES = {
  client: { bg: "var(--ec-grn-l)", color: "var(--ec-green)" },
  technical: { bg: "var(--ec-blue-l)", color: "var(--ec-blue)" },
  review: { bg: "var(--ec-amb-l)", color: "var(--ec-amber)" },
  design: { bg: "#F3F0FD", color: "#7C3AED" },
};

const OWNER_OPTIONS = ["589 Team", "Customer"];

const TaskItem = ({ task, onToggle, onOwnerChange }) => {
  const tag = TAG_STYLES[(task.category || "technical").toLowerCase()] || TAG_STYLES.technical;
  const ownerName = task.owner || "589 Team";
  const isCustomer = /customer|client/i.test(ownerName);
  const initials =
    task.owner_initials ||
    ownerName
      .split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 3)
      .toUpperCase();
  const [menuOpen, setMenuOpen] = React.useState(false);
  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "11px 18px",
        borderBottom: "1px solid var(--ec-rule)",
        position: "relative",
      }}
      data-testid={`task-item-${task.id}`}
    >
      <button
        type="button"
        aria-label={task.completed ? "Uncheck" : "Check"}
        onClick={(e) => {
          e.preventDefault();
          onToggle(task);
        }}
        style={{
          width: 18,
          height: 18,
          borderRadius: 4,
          border: task.completed ? "1.5px solid var(--ec-green)" : "1.5px solid var(--ec-rule2)",
          background: task.completed ? "var(--ec-green)" : "#fff",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          flexShrink: 0,
          marginTop: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s",
        }}
        data-testid={`task-check-${task.id}`}
      >
        {task.completed ? "✓" : ""}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: task.completed ? "var(--ec-ink4)" : "var(--ec-ink2)",
            lineHeight: 1.35,
            textDecoration: task.completed ? "line-through" : "none",
          }}
        >
          {task.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--ec-ink4)", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", position: "relative" }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 8px 2px 3px",
              borderRadius: 999,
              border: "1px solid var(--ec-rule)",
              background: "#fff",
              cursor: "pointer",
              fontSize: 11,
              color: "var(--ec-ink3)",
              lineHeight: 1,
            }}
            data-testid={`task-owner-btn-${task.id}`}
            title="Reassign owner"
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: isCustomer ? "var(--ec-blue)" : "var(--ec-amber)",
                color: "#fff",
                fontSize: 8,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {initials}
            </span>
            <span style={{ fontWeight: 600, color: "var(--ec-ink2)" }}>{ownerName}</span>
            <span style={{ fontSize: 8, color: "var(--ec-ink4)" }}>▾</span>
          </button>
          {menuOpen && (
            <>
              <div
                onClick={() => setMenuOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 40 }}
                aria-hidden
              />
              <div
                role="menu"
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  zIndex: 50,
                  background: "#fff",
                  border: "1px solid var(--ec-rule2)",
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(15,17,23,0.12)",
                  minWidth: 140,
                  padding: 4,
                }}
                data-testid={`task-owner-menu-${task.id}`}
              >
                {OWNER_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      if (opt !== ownerName) onOwnerChange(task, opt);
                    }}
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      gap: 6,
                      padding: "7px 8px",
                      borderRadius: 6,
                      border: "none",
                      background: opt === ownerName ? "var(--ec-blue-l)" : "transparent",
                      color: "var(--ec-ink2)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    data-testid={`task-owner-option-${task.id}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {opt === ownerName ? "✓" : " "} {opt}
                  </button>
                ))}
              </div>
            </>
          )}
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 4,
              background: tag.bg,
              color: tag.color,
            }}
          >
            {(task.category || "Technical").replace(/^./, (c) => c.toUpperCase())}
          </span>
          {task.due_date && <span>Due {fmtMonthDay(task.due_date)}</span>}
        </div>
      </div>
    </li>
  );
};

const PhaseCard = ({ phase, tasks, defaultExpanded, onToggleTask, onOwnerChange }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const badgeClass =
    phase.status === "complete" ? "done" : phase.status === "active" ? "active" : "pending";
  const badgeLabel =
    phase.status === "complete"
      ? "Complete"
      : phase.status === "active"
        ? "In Progress"
        : `Up Next${phase.start_date ? ` · Starts ${fmtMonthDay(phase.start_date)}` : ""}`;
  const doneCount = tasks.filter((t) => t.completed).length;
  return (
    <div className="ec-card" style={{ marginBottom: 0 }} data-testid={`phase-card-${phase.number}`}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="ec-card-header"
        style={{
          padding: "14px 18px",
          width: "100%",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
        }}
        data-testid={`phase-toggle-${phase.number}`}
        aria-expanded={expanded}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              display: "inline-block",
              fontSize: 10,
              color: "var(--ec-ink4)",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s",
              width: 10,
            }}
            aria-hidden
          >
            ▶
          </span>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ec-ink)" }}>
            Phase {phase.number} — {phase.name}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "var(--ec-ink4)" }}>
            {doneCount}/{tasks.length}
          </span>
          <span className={`ec-pill ${badgeClass}`}>{badgeLabel}</span>
        </div>
      </button>
      {expanded && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {tasks.length === 0 ? (
            <li style={{ padding: "14px 18px", fontSize: 12, color: "var(--ec-ink4)" }}>
              No tasks for this phase yet.
            </li>
          ) : (
            tasks.map((t) => (
              <TaskItem key={t.id} task={t} onToggle={onToggleTask} onOwnerChange={onOwnerChange} />
            ))
          )}
        </ul>
      )}
    </div>
  );
};

/* ───────── Milestones panel ───────── */
const MilestonesPanel = ({ milestones, doneCount }) => (
  <div className="ec-card" style={{ marginBottom: 0 }} data-testid="milestones-panel">
    <div className="ec-card-header" style={{ padding: "14px 18px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ec-ink)", display: "flex", alignItems: "center", gap: 7 }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--ec-blue)" strokeWidth="1.5">
          <path d="M8 2l1.8 5H15l-4.1 3 1.6 5L8 12.3 3.5 15l1.6-5L1 7h5.2z" />
        </svg>
        Project Milestones
      </div>
      <span style={{ fontSize: 11, color: "var(--ec-ink4)", fontWeight: 500 }}>
        {doneCount} of {milestones.length} complete
      </span>
    </div>
    {milestones.length === 0 ? (
      <div style={{ padding: "14px 18px", fontSize: 12, color: "var(--ec-ink4)" }}>No milestones yet.</div>
    ) : (
      milestones.map((m) => {
        const iconBg =
          m.status === "complete"
            ? "var(--ec-grn-l)"
            : m.status === "active" || m.status === "in_progress"
              ? "var(--ec-blue-l)"
              : "#F1F1EF";
        const iconSymbol =
          m.status === "complete" ? "✅" : m.status === "active" || m.status === "in_progress" ? "🎯" : "⬡";
        const pillClass =
          m.status === "complete"
            ? "done"
            : m.status === "active" || m.status === "in_progress"
              ? "active"
              : "pending";
        const pillLabel =
          m.status === "complete"
            ? "Done"
            : m.status === "active" || m.status === "in_progress"
              ? "In Progress"
              : "Upcoming";
        return (
          <div
            key={m.id}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: "12px 18px",
              borderBottom: "1px solid var(--ec-rule)",
            }}
            data-testid={`milestone-item-${m.id}`}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {iconSymbol}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ec-ink)", marginBottom: 2 }}>{m.name || m.title || m.week_label || "Milestone"}</div>
              <div style={{ fontSize: 11, color: "var(--ec-ink4)" }}>
                {m.status === "complete"
                  ? `Completed ${fmtDate(m.completed_at || m.updated_at)}`
                  : `Due ${fmtDate(m.due_date)}`}
              </div>
            </div>
            <span className={`ec-pill ${pillClass}`}>{pillLabel}</span>
          </div>
        );
      })
    )}
  </div>
);

/* ───────── Activity feed ───────── */
const ActivityFeed = ({ items }) => (
  <div className="ec-card" style={{ marginBottom: 0 }} data-testid="activity-feed">
    <div className="ec-card-header" style={{ padding: "14px 18px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ec-ink)", display: "flex", alignItems: "center", gap: 7 }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--ec-blue)" strokeWidth="1.5">
          <circle cx="8" cy="8" r="6" />
          <path d="M8 5v3l2 1.5" />
        </svg>
        Recent Activity
      </div>
      <span style={{ fontSize: 11, color: "var(--ec-ink4)" }}>Last 7 days</span>
    </div>
    {items.length === 0 ? (
      <div style={{ padding: "14px 18px", fontSize: 12, color: "var(--ec-ink4)" }}>No activity yet.</div>
    ) : (
      items.map((a, i) => {
        const dot =
          a.kind === "completion" ? "var(--ec-green)" :
          a.kind === "blocker" ? "var(--ec-red)" :
          a.kind === "action" ? "var(--ec-amber)" : "var(--ec-blue)";
        const initials = (a.actor_initials || (a.actor || "589")).slice(0, 3).toUpperCase();
        return (
          <div
            key={a.id || i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "11px 18px",
              borderBottom: "1px solid var(--ec-rule)",
            }}
            data-testid={`activity-item-${i}`}
          >
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flexShrink: 0, marginTop: 6 }} />
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--ec-blue-l)",
                color: "var(--ec-blue)",
                fontSize: 10,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "var(--ec-ink2)", lineHeight: 1.45 }}>
                {a.text}
              </div>
              <div style={{ fontSize: 11, color: "var(--ec-ink4)", marginTop: 2 }}>
                {fmtDate(a.at)}
              </div>
            </div>
          </div>
        );
      })
    )}
  </div>
);

/* ───────── Normalizers from backend project doc ───────── */
const deriveActivity = (updates, milestones) => {
  const events = [];
  (updates || []).forEach((u) =>
    events.push({
      id: u.id,
      at: u.created_at,
      kind: "update",
      actor: u.author_name || "Project Team",
      text: u.message || u.title || "Project update",
    })
  );
  (milestones || []).forEach((m) => {
    if (m.status === "complete" && m.completed_at) {
      events.push({
        id: `ms_${m.id}`,
        at: m.completed_at,
        kind: "completion",
        actor: "Milestone",
        text: `Milestone **${m.name || m.title || "Milestone"}** marked complete`,
      });
    }
  });
  return events
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 12);
};

const derivePhases = (milestones, currentWeekIdx) => {
  // Map each milestone to a gantt phase. This leverages the existing
  // project_milestones collection (week_number / status / name / phase).
  if (!milestones || milestones.length === 0) {
    return [
      { id: "p1", number: 1, name: "Kickoff & Access Setup", status: "pending", start_week: 0, progress: 0 },
      { id: "p2", number: 2, name: "Workflow Design", status: "pending", start_week: 1, progress: 0 },
      { id: "p3", number: 3, name: "Build & Configure", status: "pending", start_week: 2, progress: 0 },
      { id: "p4", number: 4, name: "Launch & Activate", status: "pending", start_week: 3, progress: 0 },
      { id: "p5", number: 5, name: "Measure & Validate", status: "pending", start_week: 4, progress: 0 },
    ];
  }
  return milestones.slice(0, 5).map((m, i) => {
    let status = "pending";
    if (m.status === "complete" || m.status === "done") status = "complete";
    else if (m.status === "in_progress" || i === currentWeekIdx) status = "active";
    else if (m.status === "blocked") status = "blocked";

    // progress
    const items = m.checklist_items || [];
    const doneCount = items.filter((c) => c.done).length;
    const pct = items.length ? Math.round((doneCount / items.length) * 100) : status === "complete" ? 100 : status === "active" ? 35 : 0;

    return {
      id: m.id,
      number: i + 1,
      // Support both new seed (name) and legacy seed (title).
      name: m.name || m.title || m.week_label || `Phase ${i + 1}`,
      status,
      start_week: m.week_number ?? i,
      progress: pct,
    };
  });
};

const inferOwner = (label, rawOwner) => {
  if (rawOwner && typeof rawOwner === "string" && rawOwner.trim()) return rawOwner;
  // Heuristics — tasks that read like customer responsibilities default to Customer.
  const text = (label || "").toLowerCase();
  const customerKeywords = [
    "approve", "approval", "sign-off", "sign off", "review complete",
    "system access", "systems access", "access confirmed", "data source",
    "stakeholder", "provide", "confirm", "customer review",
    "kickoff meeting", "kickoff call", "business owner",
  ];
  if (customerKeywords.some((k) => text.includes(k))) return "Customer";
  return "589 Team";
};

const deriveTasks = (milestones) => {
  const tasks = [];
  (milestones || []).forEach((m, phaseIdx) => {
    (m.checklist_items || []).forEach((c, ciIdx) => {
      const label = c.label || c.item || c.text || c.name || "Task";
      const owner = inferOwner(label, c.owner);
      tasks.push({
        id: c.id || `${m.id}_${ciIdx}`,
        name: label,
        completed: !!c.done,
        owner,
        owner_initials: c.owner_initials,
        category: c.category || (/customer|client/i.test(owner) ? "client" : "technical"),
        due_date: c.due_date,
        phase_idx: phaseIdx,
        milestone_id: m.id,
        _ci_index: ciIdx,
      });
    });
  });
  return tasks;
};

/* ───────── E-02: Weekly Status Report composer ───────── */
const StatusReportsPanel = ({ tenantId, refreshKey }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    fetch(`${BACKEND}/api/tenants/${tenantId}/project/status-reports`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        setReports(Array.isArray(d) ? d : []);
        // Item 8 (May-2026): do NOT auto-open the first report — every report
        // starts collapsed so the user can pick which one to read.
      })
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const statusMeta = (s) => {
    if (s === "on_track") return { label: "On Track", color: "var(--ec-green)", bg: "var(--ec-grn-l)" };
    if (s === "at_risk") return { label: "At Risk", color: "var(--ec-amber)", bg: "var(--ec-amb-l)" };
    if (s === "blocked") return { label: "Blocked", color: "var(--ec-red)", bg: "var(--ec-red-l)" };
    return { label: s || "Update", color: "var(--ec-ink3)", bg: "#F1F1EF" };
  };

  return (
    <div className="ec-card fade-up" style={{ marginTop: 22 }} data-testid="status-reports-panel">
      <div className="ec-card-header">
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ec-ink)", display: "flex", alignItems: "center", gap: 7 }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--ec-blue)" strokeWidth="1.5">
            <rect x="2" y="2" width="12" height="12" rx="2" />
            <path d="M5 5h6M5 8h6M5 11h3" strokeLinecap="round" />
          </svg>
          Status Reports
        </div>
        <span style={{ fontSize: 11, color: "var(--ec-ink4)" }}>
          {loading ? "Loading…" : `${reports.length} report${reports.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {!loading && reports.length === 0 && (
        <div style={{ padding: "16px 22px", fontSize: 13, color: "var(--ec-ink4)" }}>
          No status updates posted yet. Use the composer below to publish your first weekly update.
        </div>
      )}
      {reports.map((r) => {
        const meta = statusMeta(r.overall_status);
        const open = openId === r.id;
        return (
          <div
            key={r.id}
            style={{ borderBottom: "1px solid var(--ec-rule)" }}
            data-testid={`status-report-${r.id}`}
          >
            <button
              type="button"
              onClick={() => setOpenId(open ? null : r.id)}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                padding: "14px 22px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
              data-testid={`status-report-toggle-${r.id}`}
            >
              <span
                style={{
                  display: "inline-block",
                  fontSize: 10,
                  color: "var(--ec-ink4)",
                  transform: open ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.15s",
                  width: 10,
                }}
                aria-hidden
              >
                ▶
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--ec-ink)",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.title}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 9px",
                  borderRadius: 999,
                  background: meta.bg,
                  color: meta.color,
                }}
              >
                {meta.label}
              </span>
              <span style={{ fontSize: 11, color: "var(--ec-ink4)" }}>
                {fmtDate(r.date || r.created_at)}
              </span>
            </button>
            {open && (
              <div
                style={{
                  padding: "0 22px 18px 44px",
                  display: "grid",
                  gap: 14,
                  fontSize: 13,
                  color: "var(--ec-ink2)",
                  lineHeight: 1.6,
                }}
                data-testid={`status-report-body-${r.id}`}
              >
                {r.reported_by && (
                  <div style={{ fontSize: 11, color: "var(--ec-ink4)" }}>
                    Reported by <strong style={{ color: "var(--ec-ink2)" }}>{r.reported_by}</strong>
                  </div>
                )}
                {r.summary && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ec-ink4)", letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 4 }}>
                      Summary
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.summary}</div>
                  </div>
                )}
                {r.next_steps && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ec-ink4)", letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 4 }}>
                      Next Steps
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.next_steps}</div>
                  </div>
                )}
                {r.blockers && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ec-ink4)", letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 4 }}>
                      Blockers
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.blockers}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const StatusReportComposer = ({ tenantId, onPosted }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    reported_by: "",
    overall_status: "on_track",
    summary: "",
    next_steps: "",
    blockers: "",
  });

  const submit = async () => {
    if (!form.title.trim() || !form.summary.trim() || !form.reported_by.trim()) {
      toast.error("Title, reporter, and summary are required.");
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${BACKEND}/api/tenants/${tenantId}/project/status-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }).then((r) => {
        if (!r.ok) throw new Error(`API error ${r.status}`);
        return r.json();
      });
      toast.success("Status update posted to the Activity feed.");
      setForm({ title: "", reported_by: "", overall_status: "on_track", summary: "", next_steps: "", blockers: "" });
      setOpen(false);
      onPosted?.();
    } catch (e) {
      toast.error(e.message || "Could not post status update");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div
        className="fade-up"
        style={{
          marginTop: 22,
          padding: "16px 20px",
          background: "var(--ec-bg-card)",
          border: "1px dashed var(--ec-rule2)",
          borderRadius: "var(--ec-radius-lg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
        data-testid="status-report-composer-collapsed"
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ec-ink)" }}>Weekly Status Update</div>
          <div style={{ fontSize: 12, color: "var(--ec-ink4)" }}>
            Post a quick health check that surfaces in the Activity feed and customer Dashboard.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ec-btn-primary"
          style={{ padding: "10px 18px", fontSize: 13 }}
          data-testid="status-report-open-btn"
        >
          + Post Status Update
        </button>
      </div>
    );
  }

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--ec-rule2)",
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "'DM Sans', system-ui, sans-serif",
    background: "#fff",
    color: "var(--ec-ink)",
  };

  return (
    <div
      className="ec-card fade-up"
      style={{ marginTop: 22 }}
      data-testid="status-report-composer"
    >
      <div className="ec-card-header">
        <span className="ec-card-label">Post Weekly Status Update</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            fontSize: 12,
            color: "var(--ec-ink4)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
      <div className="ec-card-body" style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          <input
            value={form.title}
            onChange={(e) => setF("title", e.target.value)}
            placeholder="Title (e.g. Week 2 — Build phase progress)"
            style={inputStyle}
            data-testid="status-input-title"
          />
          <input
            value={form.reported_by}
            onChange={(e) => setF("reported_by", e.target.value)}
            placeholder="Reported by"
            style={inputStyle}
            data-testid="status-input-reporter"
          />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { key: "on_track", label: "On Track", color: "var(--ec-green)" },
            { key: "at_risk", label: "At Risk", color: "var(--ec-amber)" },
            { key: "blocked", label: "Blocked", color: "var(--ec-red)" },
          ].map((opt) => {
            const sel = form.overall_status === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setF("overall_status", opt.key)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  border: sel ? `1.5px solid ${opt.color}` : "1px solid var(--ec-rule2)",
                  background: sel ? `${opt.color}15` : "#fff",
                  color: sel ? opt.color : "var(--ec-ink3)",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                data-testid={`status-option-${opt.key}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <textarea
          value={form.summary}
          onChange={(e) => setF("summary", e.target.value)}
          rows={3}
          placeholder="Summary of progress this week"
          style={{ ...inputStyle, resize: "vertical" }}
          data-testid="status-input-summary"
        />
        <textarea
          value={form.next_steps}
          onChange={(e) => setF("next_steps", e.target.value)}
          rows={2}
          placeholder="Next steps"
          style={{ ...inputStyle, resize: "vertical" }}
          data-testid="status-input-next-steps"
        />
        <textarea
          value={form.blockers}
          onChange={(e) => setF("blockers", e.target.value)}
          rows={2}
          placeholder="Blockers (optional)"
          style={{ ...inputStyle, resize: "vertical" }}
          data-testid="status-input-blockers"
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="ec-btn-primary"
            style={{ padding: "10px 18px", fontSize: 13 }}
            data-testid="status-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Publish Update
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────── Main ───────── */
export default function ProjectTrackerV2({ tenantId, onProjectStatusChange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);

  const load = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    api(`/api/tenants/${tenantId}/project`)
      .then((d) => setData(d))
      .catch((e) => toast.error(e.message || "Failed to load project"))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const { project, milestones, updates, phases, tasks, activePhaseIdx } = useMemo(() => {
    const project = data?.project || null;
    const ms = data?.milestones || [];
    const up = data?.updates || [];
    const currentWeekIdx = (() => {
      if (!project?.start_date) return 0;
      const days = Math.floor((Date.now() - new Date(project.start_date).getTime()) / 86400000);
      return Math.max(0, Math.min(4, Math.floor(days / 7)));
    })();
    const phases = derivePhases(ms, currentWeekIdx);
    const tasks = deriveTasks(ms);
    let activePhaseIdx = phases.findIndex((p) => p.status === "active");
    if (activePhaseIdx === -1) {
      activePhaseIdx = phases.findIndex((p) => p.status === "pending");
      if (activePhaseIdx !== -1) phases[activePhaseIdx].status = "active";
    }
    if (activePhaseIdx === -1) activePhaseIdx = 0;
    return { project, milestones: ms, updates: up, phases, tasks, activePhaseIdx };
  }, [data]);

  const computed = project?._computed || {};
  const workflowCount = (data?.workflows || []).length || 0;
  const msStats = {
    total: milestones.length,
    done: milestones.filter((m) => m.status === "complete" || m.status === "done").length,
  };
  const checkStats = {
    total: computed.checklist_total || tasks.length,
    done: computed.checklist_done || tasks.filter((t) => t.completed).length,
  };
  const progressPct = project?.progress_percent ?? (checkStats.total ? Math.round((checkStats.done / checkStats.total) * 100) : 0);
  const endDate = project?.target_completion_date || project?.target_end_date;
  const daysLeft = endDate ? daysUntil(endDate) : null;

  const toggleTask = async (task) => {
    // Optimistic flip of the checklist item inside its parent milestone.
    // Match by id if present; otherwise by whichever label field is set
    // (legacy seed uses `item`, new seed uses `label`).
    const matches = (c) =>
      (c.id && task.id && c.id === task.id) ||
      c.label === task.name || c.item === task.name || c.text === task.name;

    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, milestones: (prev.milestones || []).map((m) => {
        if (m.id !== task.milestone_id) return m;
        return {
          ...m,
          checklist_items: (m.checklist_items || []).map((c) =>
            matches(c) ? { ...c, done: !task.completed } : c
          ),
        };
      }) };
      return next;
    });
    try {
      const target = (data?.milestones || []).find((m) => m.id === task.milestone_id);
      if (!target) return;
      const nextChecklist = (target.checklist_items || []).map((c) =>
        matches(c) ? { ...c, done: !task.completed } : c
      );
      await api(`/api/tenants/${tenantId}/project/milestones/${task.milestone_id}`, {
        method: "PATCH",
        body: JSON.stringify({ checklist_items: nextChecklist }),
      });
    } catch (e) {
      toast.error("Task update failed; reloading…");
      load();
    }
  };

  const changeOwner = async (task, newOwner) => {
    const matches = (c) =>
      (c.id && task.id && c.id === task.id) ||
      c.label === task.name || c.item === task.name || c.text === task.name;

    // Optimistic owner update
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        milestones: (prev.milestones || []).map((m) =>
          m.id !== task.milestone_id
            ? m
            : {
                ...m,
                checklist_items: (m.checklist_items || []).map((c) =>
                  matches(c)
                    ? {
                        ...c,
                        owner: newOwner,
                        category: /customer|client/i.test(newOwner) ? "client" : "technical",
                      }
                    : c
                ),
              }
        ),
      };
    });
    try {
      const target = (data?.milestones || []).find((m) => m.id === task.milestone_id);
      if (!target) return;
      const nextChecklist = (target.checklist_items || []).map((c) =>
        matches(c)
          ? {
              ...c,
              owner: newOwner,
              category: /customer|client/i.test(newOwner) ? "client" : "technical",
            }
          : c
      );
      await api(`/api/tenants/${tenantId}/project/milestones/${task.milestone_id}`, {
        method: "PATCH",
        body: JSON.stringify({ checklist_items: nextChecklist }),
      });
      toast.success(`Reassigned to ${newOwner}`);
    } catch (e) {
      toast.error("Owner change failed; reloading…");
      load();
    }
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api(`/api/tenants/${tenantId}/project/complete`, { method: "POST", body: "" });
      toast.success("Project marked complete.");
      if (onProjectStatusChange) onProjectStatusChange();
      load();
    } catch (e) {
      toast.error(e.message || "Failed to mark complete");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="ec-v2" data-testid="tracker-v2-loading">
        <div className="ec-content" style={{ textAlign: "center", padding: 64 }}>
          <Loader2 className="h-6 w-6 animate-spin" style={{ margin: "0 auto", color: "var(--ec-ink4)" }} />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="ec-v2" data-testid="tracker-v2-empty">
        <div className="ec-content">
          <div className="ec-card">
            <div className="ec-card-body">
              <div style={{ fontSize: 14, color: "var(--ec-ink3)", textAlign: "center", padding: 32 }}>
                Your Project Tracker unlocks once the roadmap is approved.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activity = deriveActivity(updates, milestones);

  return (
    <div className="ec-v2" data-testid="project-tracker-v2">
      <div className="ec-content">
        <Stepper />

        {/* Page header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            gap: 12,
            flexWrap: "wrap",
          }}
          data-testid="tracker-header"
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ec-ink)", marginBottom: 2 }}>
              {project.name || `${project.tenant_name || project.client_name || "AI Implementation"}: AI Implementation`}
            </div>
            <div style={{ fontSize: 12, color: "var(--ec-ink4)" }}>
              Project started {fmtDate(project.start_date)} · Target completion {fmtDate(endDate)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span
              className={`ec-pill ${project.status === "complete" ? "done" : "prep"}`}
              style={{ fontSize: 12, padding: "4px 12px" }}
            >
              {project.status === "complete" ? "Delivered" : "In Delivery"}
            </span>
            <button
              onClick={markComplete}
              disabled={completing || project.status === "complete"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
                background: "var(--ec-green)",
                border: "none",
                borderRadius: 8,
                padding: "8px 16px",
                cursor: completing ? "not-allowed" : "pointer",
                opacity: project.status === "complete" ? 0.6 : 1,
              }}
              data-testid="mark-complete-btn"
            >
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : "✓"}
              {project.status === "complete" ? "Completed" : "Mark Complete"}
            </button>
          </div>
        </div>

        <KpiStrip
          workflows={{ total: workflowCount }}
          milestones={msStats}
          checklist={checkStats}
          progress={progressPct}
          daysLeft={daysLeft}
        />

        <GanttCard phases={phases} startDate={project.start_date} endDate={endDate} />

        {/* Phase task cards */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 14,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ec-ink)", display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="var(--ec-blue)" strokeWidth="1.5">
              <path d="M13 4L6.5 11 3 7.5" />
            </svg>
            Phase Tasks
          </div>
          <span style={{ fontSize: 12, color: "var(--ec-ink4)" }}>
            Click any phase header to expand · update any task or reassign owner
          </span>
        </div>

        <div
          className="ec-phases-tasks fade-up"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            marginBottom: 22,
            animationDelay: "0.15s",
          }}
          data-testid="phases-tasks-grid"
        >
          {phases.map((p, i) => (
            <PhaseCard
              key={p.id || i}
              phase={p}
              tasks={tasks.filter((t) => t.phase_idx === i)}
              defaultExpanded={i === activePhaseIdx || i === activePhaseIdx + 1}
              onToggleTask={toggleTask}
              onOwnerChange={changeOwner}
            />
          ))}
        </div>

        {/* Milestones + Activity */}
        <div
          className="ec-bottom-grid fade-up"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, animationDelay: "0.2s" }}
        >
          <MilestonesPanel milestones={milestones} doneCount={msStats.done} />
          <ActivityFeed items={activity} />
        </div>

        {/* E-02 / Item 7 (May-2026): Status Reports panel — full report visible */}
        <StatusReportsPanel tenantId={tenantId} refreshKey={statusRefreshKey} />

        {/* Weekly Status Report composer */}
        <StatusReportComposer tenantId={tenantId} onPosted={() => { load(); setStatusRefreshKey((k) => k + 1); }} />

        <style>{`
          @media (max-width: 1024px) {
            .ec-v2 .ec-phases-tasks,
            .ec-v2 .ec-bottom-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </div>
  );
}
